import { FilterOptionsState } from '@mui/material/useAutocomplete';
import SimpleAutoComplete from 'components/SimpleAutoComplete';
import { HOUR_IN_MILLISECONDS, PAGINATION_DEFAULT_PAGE_SIZE } from 'config';
import { i18n } from 'i18n';
import {
  BackfillRecipeDto,
  usePaginatedRecipesQuery
} from 'providers/api';
import React from 'react';
import { useScenario } from 'modules/project/providers/scenario';

interface RecipeFieldProps {
  stopeId: string;
  projectId: string;
  touched?: boolean;
  error?: string
  disabledOptions?: string[];
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  onChange: (recipe: BackfillRecipeDto | null) => void;
  onBlur?: () => void;
}

const RecipeField = ({
  stopeId,
  projectId,
  touched,
  error,
  disabled,
  required,
  autoFocus,
  disabledOptions = [],
  onBlur,
  onChange,
}: RecipeFieldProps) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [value, setValue] = React.useState<BackfillRecipeDto | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string | null>(null);
  const { activeScenarioId } = useScenario();

  const {
    data,
    isFetching,
  } = usePaginatedRecipesQuery({
    stopeId,
    projectId,
    page: 1,
    pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
    searchTerm: searchTerm ?? undefined,
    scenarioId: activeScenarioId ?? undefined,
  }, {
    enabled: open,
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const getDisabledOptions = (backfillRecipe: BackfillRecipeDto) => disabledOptions.includes(backfillRecipe.entityId);
  const getOptionLabel = (backfillRecipe: BackfillRecipeDto) => backfillRecipe.reference;
  const getOptionSelected = (backfillRecipe: BackfillRecipeDto, recipeValue: BackfillRecipeDto) => backfillRecipe.entityId === recipeValue.entityId;

  const filterOptions = (recipes: BackfillRecipeDto[], state: FilterOptionsState<BackfillRecipeDto>) => recipes.filter((r) => {
    const referenceLower = r.reference.toLowerCase();
    const inputLower = state.inputValue.toLowerCase();
    return referenceLower.includes(inputLower);
  });
  const noOptionsText = searchTerm ? i18n._('No recipes matching').concat(` "${searchTerm}"`) : i18n._('No recipes');

  const handleOnChange = (selectedRecipe: BackfillRecipeDto) => {
    setValue(selectedRecipe);
    onChange(selectedRecipe);
  };

  /**
   * Default options with initial data if available
   */
  const options = data?.items;

  return (
    <SimpleAutoComplete
      label={i18n._('Recipe')}
      value={value}
      autoFocus={autoFocus}
      disabled={disabled}
      options={options ?? []}
      loading={isFetching}
      getOptionDisabled={getDisabledOptions}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      onChange={handleOnChange}
      onSearch={(term: string) => setSearchTerm(term)}
      onBlur={onBlur}
      touched={touched}
      error={error}
      required={required}
      open={open}
      onOpen={(isOpen: boolean) => setOpen(isOpen)}
    />
  );
};

export default RecipeField;
